import { useState, useEffect, useCallback } from "react";
import {
  Building2,
  Plus,
  Copy,
  ExternalLink,
  Users,
  TrendingUp,
  CheckCircle2,
  Clock,
  Search,
  RefreshCw,
  Sparkles,
  ShieldCheck,
  AlertCircle,
  Phone,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { getSupabaseClient } from "../providers/supabase/supabase";
import { useCRM } from "../context/CRMContext";
import type { CRMRecord } from "../types";

export const CrmManagementPage = () => {
  const { switchCrm } = useCRM();
  const [crms, setCrms] = useState<CRMRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);
  const [createdCrmInfo, setCreatedCrmInfo] = useState<{
    id: string;
    name: string;
    slug: string;
    token: string;
  } | null>(null);

  // Formulário de criação
  const [newName, setNewName] = useState("");
  const [newSlug, setNewSlug] = useState("");
  const [enableVoip, setEnableVoip] = useState(false);
  const [enableTranscription, setEnableTranscription] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const fetchCrms = useCallback(async () => {
    setIsLoading(true);
    try {
      const client = getSupabaseClient();
      const { data, error } = await client
        .from("crms")
        .select(
          `
          *,
          crm_members(count),
          deals(count)
        `,
        )
        .order("created_at", { ascending: false });

      if (error) throw error;

      if (data) {
        // Enriquecer dados dos donos
        const enriched = data.map((item: any) => ({
          ...item,
          member_count: item.crm_members?.[0]?.count || 0,
          deals_count: item.deals?.[0]?.count || 0,
        }));
        setCrms(enriched);
      }
    } catch (err) {
      console.error("Erro ao carregar CRMs:", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCrms();
  }, [fetchCrms]);

  // Gerador automático de slug a partir do nome
  const handleNameChange = (val: string) => {
    setNewName(val);
    const slug = val
      .toLowerCase()
      .trim()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
    setNewSlug(slug);
  };

  const handleCreateCrm = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError("");
    if (!newName.trim() || !newSlug.trim()) {
      setFormError("Preencha o nome e o identificador (slug) do CRM.");
      return;
    }

    setIsSubmitting(true);
    try {
      const client = getSupabaseClient();
      let createdId = "";

      // Chama a função RPC segura create_crm_with_defaults
      const { data, error } = await client.rpc("create_crm_with_defaults", {
        p_name: newName.trim(),
        p_slug: newSlug.trim(),
      });

      if (error) {
        // Fallback caso a RPC ainda não esteja implantada
        const token =
          "act_" +
          Math.random().toString(36).substring(2, 15) +
          Math.random().toString(36).substring(2, 15);
        const { data: insertData, error: insertError } = await client
          .from("crms")
          .insert({
            name: newName.trim(),
            slug: newSlug.trim(),
            activation_token: token,
            status: "pending_activation",
            settings: {
              voip_enabled: enableVoip,
              transcription_enabled: enableTranscription,
            },
          })
          .select()
          .single();

        if (insertError) throw insertError;

        createdId = insertData.id;
        setCreatedCrmInfo({
          id: insertData.id,
          name: insertData.name,
          slug: insertData.slug,
          token: insertData.activation_token,
        });
      } else {
        createdId = data.id;
        setCreatedCrmInfo({
          id: data.id,
          name: data.name,
          slug: data.slug,
          token: data.activation_token,
        });
      }

      // Salva as configurações de módulos no CRM recém-criado
      if (createdId) {
        await client
          .from("crms")
          .update({
            settings: {
              voip_enabled: enableVoip,
              transcription_enabled: enableTranscription,
            },
          })
          .eq("id", createdId);
      }

      setIsCreateOpen(false);
      setIsSuccessOpen(true);
      setNewName("");
      setNewSlug("");
      setEnableVoip(false);
      setEnableTranscription(false);
      await fetchCrms();
      toast.success("Novo CRM criado com sucesso!");
    } catch (err: any) {
      console.error("Erro ao criar CRM:", err);
      setFormError(err.message || "Erro ao criar novo CRM.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleModule = async (
    crmId: string,
    key: "voip_enabled" | "transcription_enabled",
    newValue: boolean,
  ) => {
    try {
      const client = getSupabaseClient();
      const targetCrm = crms.find((c) => c.id === crmId);
      const currentSettings = targetCrm?.settings || {};
      const updatedSettings = {
        ...currentSettings,
        [key]: newValue,
      };

      const { error } = await client
        .from("crms")
        .update({ settings: updatedSettings })
        .eq("id", crmId);

      if (error) throw error;

      setCrms((prev) =>
        prev.map((c) =>
          c.id === crmId ? { ...c, settings: updatedSettings } : c,
        ),
      );

      const moduleName = key === "voip_enabled" ? "VoIP" : "Transcrição IA";
      toast.success(
        `${moduleName} ${newValue ? "ativado" : "desativado"} para ${targetCrm?.name || "o CRM"}.`,
      );
    } catch (err: any) {
      console.error("Erro ao alterar módulo do CRM:", err);
      toast.error(
        "Falha ao atualizar módulo: " + (err.message || "Erro desconhecido"),
      );
    }
  };

  const getActivationLink = (token: string | null | undefined) => {
    if (!token) return "";
    const origin =
      typeof window !== "undefined"
        ? window.location.origin
        : "https://app.dotuscrm.com";
    return `${origin}/#/activate-crm?token=${encodeURIComponent(token)}`;
  };

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2500);
  };

  const filteredCrms = crms.filter(
    (c) =>
      c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.slug.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const totalActive = crms.filter((c) => c.status === "active").length;
  const totalPending = crms.filter(
    (c) => c.status === "pending_activation",
  ).length;

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6 animate-in fade-in-50 duration-300">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-border/60">
        <div>
          <div className="flex items-center gap-2">
            <span className="p-1.5 rounded-lg bg-primary/10 text-primary">
              <ShieldCheck className="w-5 h-5" />
            </span>
            <h1 className="text-2xl font-bold tracking-tight text-foreground">
              Painel de Administração de CRMs
            </h1>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Crie, ative e acesse todos os ambientes de CRM da plataforma de
            forma 100% isolada e independente.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchCrms}
            disabled={isLoading}
            className="gap-2"
          >
            <RefreshCw
              className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`}
            />
            Atualizar
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={() => setIsCreateOpen(true)}
            className="gap-2 shadow-sm"
          >
            <Plus className="w-4 h-4" />
            Criar Novo CRM
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-border/60 bg-card/60 backdrop-blur-xs">
          <CardHeader className="p-4 pb-2 flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Total de CRMs
            </CardTitle>
            <Building2 className="w-4 h-4 text-primary" />
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div className="text-2xl font-bold">{crms.length}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Ambientes cadastrados
            </p>
          </CardContent>
        </Card>

        <Card className="border-border/60 bg-card/60 backdrop-blur-xs">
          <CardHeader className="p-4 pb-2 flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              CRMs Ativos
            </CardTitle>
            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
              {totalActive}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Com dono ativado e operando
            </p>
          </CardContent>
        </Card>

        <Card className="border-border/60 bg-card/60 backdrop-blur-xs">
          <CardHeader className="p-4 pb-2 flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Aguardando Ativação
            </CardTitle>
            <Clock className="w-4 h-4 text-amber-500" />
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">
              {totalPending}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Links de primeiro acesso gerados
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Tabela de CRMs */}
      <Card className="border-border/60 bg-card/60">
        <CardHeader className="p-4 border-b border-border/60">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <CardTitle className="text-base font-semibold">
                Ambientes de CRM
              </CardTitle>
              <CardDescription className="text-xs">
                Selecione qualquer CRM para acessar com visão total de
                administrador ou copie o link de onboarding.
              </CardDescription>
            </div>
            <div className="relative w-full sm:w-64">
              <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-muted-foreground" />
              <Input
                placeholder="Buscar por nome ou slug..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 h-9 text-xs"
              />
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="border-b border-border/60 bg-muted/40 font-semibold text-muted-foreground">
                  <th className="py-3 px-4">Nome do CRM</th>
                  <th className="py-3 px-4">Identificador (Slug)</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4">Módulos (VoIP / IA)</th>
                  <th className="py-3 px-4">Membros</th>
                  <th className="py-3 px-4">Negócios</th>
                  <th className="py-3 px-4">Criado em</th>
                  <th className="py-3 px-4 text-right">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/40">
                {isLoading ? (
                  <tr>
                    <td colSpan={8} className="text-center py-12">
                      <div className="flex items-center justify-center gap-2 text-muted-foreground">
                        <RefreshCw className="w-4 h-4 animate-spin text-primary" />
                        <span>Carregando ambientes de CRM...</span>
                      </div>
                    </td>
                  </tr>
                ) : filteredCrms.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="text-center py-12 text-muted-foreground"
                    >
                      Nenhum CRM encontrado.
                    </td>
                  </tr>
                ) : (
                  filteredCrms.map((crm) => {
                    const activationUrl = getActivationLink(
                      crm.activation_token,
                    );
                    return (
                      <tr
                        key={crm.id}
                        className="hover:bg-muted/30 transition-colors group"
                      >
                        <td className="py-3 px-4 font-medium text-foreground">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-md bg-primary/10 text-primary flex items-center justify-center font-bold text-xs uppercase">
                              {crm.name.slice(0, 2)}
                            </div>
                            <div>
                              <div className="font-semibold text-foreground">
                                {crm.name}
                              </div>
                              <div className="text-[10px] text-muted-foreground font-mono">
                                ID: {crm.id.slice(0, 8)}...
                              </div>
                            </div>
                          </div>
                        </td>

                        <td className="py-3 px-4 font-mono text-[11px] text-muted-foreground">
                          {crm.slug}
                        </td>

                        <td className="py-3 px-4">
                          {crm.status === "active" ? (
                            <Badge
                              variant="outline"
                              className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20 text-[10px] font-medium"
                            >
                              <CheckCircle2 className="w-3 h-3 mr-1" />
                              Ativo
                            </Badge>
                          ) : (
                            <Badge
                              variant="outline"
                              className="bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20 text-[10px] font-medium"
                            >
                              <Clock className="w-3 h-3 mr-1" />
                              Aguardando Dono
                            </Badge>
                          )}
                        </td>

                        <td className="py-3 px-4">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {/* Toggle VoIP */}
                            <button
                              type="button"
                              onClick={() =>
                                handleToggleModule(
                                  crm.id,
                                  "voip_enabled",
                                  !crm.settings?.voip_enabled,
                                )
                              }
                              title={
                                crm.settings?.voip_enabled
                                  ? "VoIP Ativo (clique para desativar)"
                                  : "VoIP Inativo (clique para ativar)"
                              }
                              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium transition-all border cursor-pointer ${
                                crm.settings?.voip_enabled
                                  ? "bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/30 hover:bg-blue-500/25"
                                  : "bg-muted/70 text-muted-foreground border-border/50 opacity-60 hover:opacity-100 hover:border-border"
                              }`}
                            >
                              <Phone className="w-3 h-3" />
                              <span>VoIP</span>
                              <span className="text-[9px] font-bold">
                                {crm.settings?.voip_enabled ? "ON" : "OFF"}
                              </span>
                            </button>

                            {/* Toggle Transcrição IA */}
                            <button
                              type="button"
                              onClick={() =>
                                handleToggleModule(
                                  crm.id,
                                  "transcription_enabled",
                                  !crm.settings?.transcription_enabled,
                                )
                              }
                              title={
                                crm.settings?.transcription_enabled
                                  ? "Transcrição IA Ativa (clique para desativar)"
                                  : "Transcrição IA Inativa (clique para ativar)"
                              }
                              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium transition-all border cursor-pointer ${
                                crm.settings?.transcription_enabled
                                  ? "bg-purple-500/15 text-purple-600 dark:text-purple-400 border-purple-500/30 hover:bg-purple-500/25"
                                  : "bg-muted/70 text-muted-foreground border-border/50 opacity-60 hover:opacity-100 hover:border-border"
                              }`}
                            >
                              <Sparkles className="w-3 h-3" />
                              <span>IA</span>
                              <span className="text-[9px] font-bold">
                                {crm.settings?.transcription_enabled
                                  ? "ON"
                                  : "OFF"}
                              </span>
                            </button>
                          </div>
                        </td>

                        <td className="py-3 px-4 text-muted-foreground">
                          <div className="flex items-center gap-1.5">
                            <Users className="w-3.5 h-3.5 opacity-70" />
                            <span>{crm.member_count || 1}</span>
                          </div>
                        </td>

                        <td className="py-3 px-4 text-muted-foreground">
                          <div className="flex items-center gap-1.5">
                            <TrendingUp className="w-3.5 h-3.5 opacity-70" />
                            <span>{crm.deals_count || 0}</span>
                          </div>
                        </td>

                        <td className="py-3 px-4 text-muted-foreground">
                          {new Date(crm.created_at).toLocaleDateString("pt-BR")}
                        </td>

                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            {crm.activation_token && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  copyToClipboard(activationUrl, crm.id)
                                }
                                title="Copiar Link de Ativação do Dono"
                                className="h-7 text-xs px-2 gap-1 border-amber-500/30 text-amber-700 dark:text-amber-300 hover:bg-amber-500/10"
                              >
                                {copiedId === crm.id ? (
                                  <>
                                    <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                                    <span>Copiado!</span>
                                  </>
                                ) : (
                                  <>
                                    <Copy className="w-3 h-3" />
                                    <span>Link Dono</span>
                                  </>
                                )}
                              </Button>
                            )}

                            <Button
                              variant="default"
                              size="sm"
                              onClick={() => switchCrm(crm.id)}
                              className="h-7 text-xs px-2.5 gap-1 shadow-xs"
                            >
                              <ExternalLink className="w-3 h-3" />
                              <span>Acessar CRM</span>
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Modal de Criação de Novo CRM */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="sm:max-w-md">
          <form onSubmit={handleCreateCrm}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-primary" />
                Criar Novo CRM
              </DialogTitle>
              <DialogDescription className="text-xs">
                O CRM nascerá sem membros e com um link de ativação exclusivo. O
                primeiro usuário que abrir o link será o Dono oficial com
                controle total.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {formError && (
                <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-xs flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  <span>{formError}</span>
                </div>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="crmName" className="text-xs font-medium">
                  Nome da Empresa / CRM *
                </Label>
                <Input
                  id="crmName"
                  placeholder="Ex: Acme Tecnologia"
                  value={newName}
                  onChange={(e) => handleNameChange(e.target.value)}
                  required
                  className="h-9 text-xs"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="crmSlug" className="text-xs font-medium">
                  Identificador Único (Slug) *
                </Label>
                <Input
                  id="crmSlug"
                  placeholder="Ex: acme-tecnologia"
                  value={newSlug}
                  onChange={(e) => setNewSlug(e.target.value)}
                  required
                  className="h-9 text-xs font-mono"
                />
                <p className="text-[10px] text-muted-foreground">
                  Usado para identificação segura do tenant no banco e rotas.
                </p>
              </div>

              {/* Módulos opcionais do CRM */}
              <div className="space-y-2.5 pt-1">
                <Label className="text-xs font-semibold text-foreground">
                  Módulos Adicionais (Ativar sob demanda)
                </Label>

                {/* Switch VoIP */}
                <div className="flex items-center justify-between p-2.5 rounded-lg border border-border/70 bg-muted/20 hover:bg-muted/40 transition-colors">
                  <div className="flex items-start gap-2.5 pr-2">
                    <span className="p-1 rounded-md bg-blue-500/10 text-blue-600 dark:text-blue-400 mt-0.5">
                      <Phone className="w-3.5 h-3.5" />
                    </span>
                    <div>
                      <Label
                        htmlFor="enableVoip"
                        className="text-xs font-medium cursor-pointer"
                      >
                        Habilitar Telefonia VoIP (API4COM)
                      </Label>
                      <p className="text-[11px] text-muted-foreground leading-snug">
                        Permite que os usuários deste CRM façam ligações pelo
                        discador integrado.
                      </p>
                    </div>
                  </div>
                  <Switch
                    id="enableVoip"
                    checked={enableVoip}
                    onCheckedChange={setEnableVoip}
                  />
                </div>

                {/* Switch Transcrição IA */}
                <div className="flex items-center justify-between p-2.5 rounded-lg border border-border/70 bg-muted/20 hover:bg-muted/40 transition-colors">
                  <div className="flex items-start gap-2.5 pr-2">
                    <span className="p-1 rounded-md bg-purple-500/10 text-purple-600 dark:text-purple-400 mt-0.5">
                      <Sparkles className="w-3.5 h-3.5" />
                    </span>
                    <div>
                      <Label
                        htmlFor="enableTranscription"
                        className="text-xs font-medium cursor-pointer"
                      >
                        Habilitar Transcrição & Auditoria IA
                      </Label>
                      <p className="text-[11px] text-muted-foreground leading-snug">
                        Transcreve áudios com Whisper e gera auditorias
                        comerciais completas com Gemini.
                      </p>
                    </div>
                  </div>
                  <Switch
                    id="enableTranscription"
                    checked={enableTranscription}
                    onCheckedChange={setEnableTranscription}
                  />
                </div>
              </div>

              <div className="p-3 rounded-lg bg-muted/40 border border-border/60 text-xs space-y-1.5">
                <div className="font-semibold text-foreground flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5 text-primary" />O que
                  será configurado automaticamente:
                </div>
                <ul className="text-muted-foreground text-[11px] list-disc list-inside space-y-0.5">
                  <li>Isolamento absoluto de dados no banco de dados (RLS).</li>
                  <li>Funil de vendas padrão com 6 etapas de pipeline.</li>
                  <li>Token criptográfico para ativação do Dono.</li>
                </ul>
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setIsCreateOpen(false)}
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                size="sm"
                disabled={isSubmitting}
                className="gap-2"
              >
                {isSubmitting ? (
                  <>
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    Criando...
                  </>
                ) : (
                  <>
                    <Plus className="w-3.5 h-3.5" />
                    Criar CRM
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal de Sucesso com Link de Ativação */}
      <Dialog open={isSuccessOpen} onOpenChange={setIsSuccessOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400">
              <CheckCircle2 className="w-5 h-5" />
              CRM Criado com Sucesso!
            </DialogTitle>
            <DialogDescription className="text-xs">
              O ambiente{" "}
              <strong className="text-foreground">
                {createdCrmInfo?.name}
              </strong>{" "}
              foi criado. Envie o link abaixo para o primeiro usuário assumir o
              controle como Dono.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">
                Link de Ativação do Dono
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  readOnly
                  value={getActivationLink(createdCrmInfo?.token)}
                  className="h-9 text-xs font-mono bg-muted/40"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    copyToClipboard(
                      getActivationLink(createdCrmInfo?.token),
                      "success_modal",
                    )
                  }
                  className="h-9 px-3 gap-1.5 shrink-0"
                >
                  {copiedId === "success_modal" ? (
                    <>
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                      <span>Copiado!</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-3.5 h-3.5" />
                      <span>Copiar Link</span>
                    </>
                  )}
                </Button>
              </div>
            </div>

            <div className="p-3.5 rounded-lg bg-amber-500/10 border border-amber-500/20 text-xs text-amber-900 dark:text-amber-200 space-y-1">
              <div className="font-semibold flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
                Como funciona a ativação:
              </div>
              <p className="text-[11px] leading-relaxed">
                Ao abrir este link, o usuário poderá cadastrar sua senha (ou
                fazer login) e será imediatamente registrado como{" "}
                <strong>Dono oficial do CRM</strong>. Depois disso, ele poderá
                convidar sua própria equipe de vendas.
              </p>
            </div>
          </div>

          <DialogFooter className="flex items-center justify-between sm:justify-between w-full">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setIsSuccessOpen(false)}
            >
              Fechar
            </Button>
            {createdCrmInfo && (
              <Button
                type="button"
                size="sm"
                onClick={() => {
                  setIsSuccessOpen(false);
                  switchCrm(createdCrmInfo.id);
                }}
                className="gap-1.5"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                Acessar este CRM agora
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
